/**
 * MyAssignmentsPage - Crew member view of task assignments
 * PROMPT 35: Crew Task Acceptance Workflow (Web)
 * PROMPT 36: Seen vs Unseen State for Task Requests
 */
import { useState, useEffect, useCallback, useRef } from "react";
import {
  getStaffList,
  getCrewAssignments,
  acceptTaskAssignment,
  rejectTaskAssignment,
  markAssignmentAsSeen,
  extractErrorMessage,
} from "../lib/api";
import {
  LoadingSpinner,
  EmptyState,
} from "../components/shared/DataComponents";
import { useFormatters } from "../components/shared/useFormatters";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import {
  User,
  Calendar,
  Clock,
  MapPin,
  FolderKanban,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  ClipboardList,
  Info,
} from "lucide-react";
import { cn } from "../lib/utils";

/**
 * Status badge component
 */
function StatusBadge({ status }) {
  const config = {
    pending: {
      label: "Pending",
      className: "bg-yellow-500/10 text-yellow-600 border-yellow-500/30",
      icon: Clock,
    },
    accepted: {
      label: "Accepted",
      className: "bg-green-500/10 text-green-600 border-green-500/30",
      icon: CheckCircle2,
    },
    rejected: {
      label: "Rejected",
      className: "bg-red-500/10 text-red-600 border-red-500/30",
      icon: XCircle,
    },
  };

  const statusConfig = config[status] || config.pending;
  const Icon = statusConfig.icon;

  return (
    <Badge variant="outline" className={cn("gap-1", statusConfig.className)}>
      <Icon className="h-3 w-3" />
      {statusConfig.label}
    </Badge>
  );
}

/**
 * Assignment card component
 */
function AssignmentCard({ assignment, onAccept, onReject, loading }) {
  const { formatDate, formatTime } = useFormatters();
  const isPending = assignment.status === "pending";

  return (
    <Card 
      className={cn(
        "transition-all",
        isPending && "border-yellow-500/30 bg-yellow-500/5"
      )}
      data-testid={`assignment-card-${assignment.id}`}
    >
      <CardContent className="p-4">
        <div className="flex flex-col gap-4">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-lg">{assignment.task_name}</h3>
              <p className="text-sm text-muted-foreground">
                {assignment.shift_name || "Unnamed Shift"}
              </p>
            </div>
            <StatusBadge status={assignment.status} />
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>{formatDate(assignment.shift_date)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>
                {formatTime(assignment.shift_start_time)} - {formatTime(assignment.shift_end_time)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <FolderKanban className="h-4 w-4 text-muted-foreground" />
              <span>{assignment.project_name || "N/A"}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span>{assignment.location_name || "N/A"}</span>
            </div>
          </div>

          {/* Rejection reason if rejected */}
          {assignment.status === "rejected" && assignment.rejection_reason && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-md p-3">
              <p className="text-sm text-red-600">
                <strong>Rejection reason:</strong> {assignment.rejection_reason}
              </p>
            </div>
          )}

          {/* Actions for pending assignments */}
          {isPending && (
            <>
              <Separator />
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => onReject(assignment)}
                  disabled={loading}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`reject-btn-${assignment.id}`}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
                <Button
                  onClick={() => onAccept(assignment)}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid={`accept-btn-${assignment.id}`}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Accept
                </Button>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function MyAssignmentsPage() {
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [activeTab, setActiveTab] = useState("pending");
  
  // Rejection dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingAssignment, setRejectingAssignment] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  // Load staff list
  useEffect(() => {
    const loadStaff = async () => {
      try {
        const res = await getStaffList({ status: "active", limit: 100 });
        // Handle paginated response format
        const staffData = res.data?.items || res.data || [];
        setStaffList(staffData);
        // Auto-select first staff if available
        if (staffData.length > 0) {
          setSelectedStaffId(staffData[0].id);
        }
      } catch (err) {
        console.error("Failed to load staff:", extractErrorMessage(err));
        toast.error("Failed to load crew list");
      } finally {
        setLoading(false);
      }
    };
    loadStaff();
  }, []);

  // Mark unseen assignments as seen
  const markUnseenAsSeen = useCallback(async (assignmentsList, staffId) => {
    const unseenAssignments = assignmentsList.filter((a) => !a.seen_at);
    if (unseenAssignments.length === 0) return;

    // Mark each unseen assignment as seen (sequential to avoid rate limiting)
    for (const assignment of unseenAssignments) {
      try {
        await markAssignmentAsSeen(assignment.id, staffId);
      } catch (err) {
        // Silent fail - seen state is non-critical
        console.warn(`Failed to mark assignment ${assignment.id} as seen:`, extractErrorMessage(err));
      }
    }
  }, []);

  // Load assignments when staff selected
  const loadAssignments = useCallback(async () => {
    if (!selectedStaffId) return;

    try {
      setLoadingAssignments(true);
      const res = await getCrewAssignments(selectedStaffId);
      const loadedAssignments = res.data || [];
      setAssignments(loadedAssignments);
      
      // Mark unseen assignments as seen (non-blocking)
      markUnseenAsSeen(loadedAssignments, selectedStaffId);
    } catch (err) {
      console.error("Failed to load assignments:", extractErrorMessage(err));
      toast.error("Failed to load assignments");
    } finally {
      setLoadingAssignments(false);
    }
  }, [selectedStaffId, markUnseenAsSeen]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  // Filter assignments by status
  const pendingAssignments = assignments.filter((a) => a.status === "pending");
  const acceptedAssignments = assignments.filter((a) => a.status === "accepted");
  const rejectedAssignments = assignments.filter((a) => a.status === "rejected");

  // Handle accept
  const handleAccept = async (assignment) => {
    try {
      setProcessingId(assignment.id);
      await acceptTaskAssignment(assignment.id, selectedStaffId);
      toast.success("Assignment accepted");
      await loadAssignments();
    } catch (err) {
      toast.error(`Failed to accept: ${extractErrorMessage(err)}`);
    } finally {
      setProcessingId(null);
    }
  };

  // Open reject dialog
  const handleRejectClick = (assignment) => {
    setRejectingAssignment(assignment);
    setRejectionReason("");
    setRejectDialogOpen(true);
  };

  // Confirm rejection
  const handleConfirmReject = async () => {
    if (!rejectingAssignment) return;

    try {
      setProcessingId(rejectingAssignment.id);
      await rejectTaskAssignment(
        rejectingAssignment.id,
        selectedStaffId,
        rejectionReason || null
      );
      toast.success("Assignment rejected");
      setRejectDialogOpen(false);
      setRejectingAssignment(null);
      await loadAssignments();
    } catch (err) {
      toast.error(`Failed to reject: ${extractErrorMessage(err)}`);
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const selectedStaff = staffList.find((s) => s.id === selectedStaffId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">My Assignments</h1>
        <p className="text-muted-foreground">
          View and respond to your task assignments
        </p>
      </div>

      {/* Crew Selector (Testing Only) */}
      <Card className="border-dashed border-amber-500/50 bg-amber-500/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2 text-amber-600">
            <Info className="h-4 w-4" />
            Testing Mode
          </CardTitle>
          <CardDescription>
            This selector simulates viewing assignments as different crew members.
            In production, this would use authentication.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Label className="text-sm whitespace-nowrap">View assignments as:</Label>
            <Select
              value={selectedStaffId}
              onValueChange={setSelectedStaffId}
            >
              <SelectTrigger className="w-[300px]" data-testid="staff-selector">
                <SelectValue placeholder="Select crew member..." />
              </SelectTrigger>
              <SelectContent>
                {staffList.map((staff) => (
                  <SelectItem key={staff.id} value={staff.id}>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {staff.first_name} {staff.last_name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedStaff && (
            <p className="text-xs text-muted-foreground mt-2">
              Email: {selectedStaff.email} | Phone: {selectedStaff.phone || "N/A"}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Assignments */}
      {!selectedStaffId ? (
        <EmptyState
          title="Select a crew member"
          description="Choose a crew member above to view their assignments"
          icon={User}
        />
      ) : loadingAssignments ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : assignments.length === 0 ? (
        <EmptyState
          title="No assignments"
          description={`${selectedStaff?.first_name || "This crew member"} has no task assignments yet`}
          icon={ClipboardList}
        />
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="pending" className="gap-2" data-testid="tab-pending">
              Pending
              {pendingAssignments.length > 0 && (
                <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-600">
                  {pendingAssignments.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="accepted" className="gap-2" data-testid="tab-accepted">
              Accepted
              {acceptedAssignments.length > 0 && (
                <Badge variant="secondary" className="bg-green-500/20 text-green-600">
                  {acceptedAssignments.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="rejected" className="gap-2" data-testid="tab-rejected">
              Rejected
              {rejectedAssignments.length > 0 && (
                <Badge variant="secondary" className="bg-red-500/20 text-red-600">
                  {rejectedAssignments.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">
              All ({assignments.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-4 space-y-4">
            {pendingAssignments.length === 0 ? (
              <EmptyState
                title="No pending assignments"
                description="You're all caught up!"
                icon={CheckCircle2}
              />
            ) : (
              pendingAssignments.map((assignment) => (
                <AssignmentCard
                  key={assignment.id}
                  assignment={assignment}
                  onAccept={handleAccept}
                  onReject={handleRejectClick}
                  loading={processingId === assignment.id}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="accepted" className="mt-4 space-y-4">
            {acceptedAssignments.length === 0 ? (
              <EmptyState
                title="No accepted assignments"
                description="Assignments you accept will appear here"
                icon={CheckCircle2}
              />
            ) : (
              acceptedAssignments.map((assignment) => (
                <AssignmentCard
                  key={assignment.id}
                  assignment={assignment}
                  onAccept={handleAccept}
                  onReject={handleRejectClick}
                  loading={processingId === assignment.id}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="rejected" className="mt-4 space-y-4">
            {rejectedAssignments.length === 0 ? (
              <EmptyState
                title="No rejected assignments"
                description="Assignments you reject will appear here"
                icon={XCircle}
              />
            ) : (
              rejectedAssignments.map((assignment) => (
                <AssignmentCard
                  key={assignment.id}
                  assignment={assignment}
                  onAccept={handleAccept}
                  onReject={handleRejectClick}
                  loading={processingId === assignment.id}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="all" className="mt-4 space-y-4">
            {assignments.map((assignment) => (
              <AssignmentCard
                key={assignment.id}
                assignment={assignment}
                onAccept={handleAccept}
                onReject={handleRejectClick}
                loading={processingId === assignment.id}
              />
            ))}
          </TabsContent>
        </Tabs>
      )}

      {/* Rejection Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Reject Assignment
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to reject this task assignment? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {rejectingAssignment && (
            <div className="bg-surface-elevated/50 rounded-md p-3 text-sm">
              <p className="font-medium">{rejectingAssignment.task_name}</p>
              <p className="text-muted-foreground">
                {rejectingAssignment.shift_name} • {rejectingAssignment.project_name}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="rejection-reason">Reason (optional)</Label>
            <Textarea
              id="rejection-reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Provide a reason for rejecting this assignment..."
              rows={3}
              data-testid="rejection-reason-input"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectDialogOpen(false)}
              disabled={processingId !== null}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmReject}
              disabled={processingId !== null}
              data-testid="confirm-reject-btn"
            >
              {processingId ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4 mr-2" />
              )}
              Reject Assignment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
